// Meta (Facebook) Pixel integration utility
// This file provides functions to track various conversion events

let sessionStartTime = null;
let sessionTimeoutId = null;
let pageVisitStartTime = null;

/**
 * Initialize Meta Pixel
 * Call this function once when the app loads
 */
export const initMetaPixel = () => {
  const pixelId = process.env.REACT_APP_META_PIXEL_ID;
  
  if (!pixelId) {
    console.warn('Meta Pixel ID not configured. Set REACT_APP_META_PIXEL_ID in your environment variables.');
    return;
  }

  // Check if pixel is already initialized
  if (window.fbq) {
    console.log('Meta Pixel already initialized');
    return;
  }

  // Meta Pixel Base Code
  (function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js'
  );

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');

  // Initialize session tracking
  initializeSessionTracking();

  console.log('Meta Pixel initialized with ID:', pixelId);
};

/**
 * Initialize session tracking
 */
function initializeSessionTracking() {
  // Record the start time of the session
  sessionStartTime = Date.now();
  pageVisitStartTime = Date.now();
  
  // Track initial page view
  trackPageView();
  
  // Track when user leaves the page
  window.addEventListener('beforeunload', trackPageLeave);
  
  // Track visibility changes (when user switches tabs)
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Set up periodic heartbeat tracking
  setupHeartbeatTracking();
}

/**
 * Track when user leaves the page
 */
function trackPageLeave() {
  const visitDuration = (Date.now() - pageVisitStartTime) / 1000; // in seconds
  const sessionDuration = (Date.now() - sessionStartTime) / 1000; // in seconds
  
  if (window.fbq) {
    window.fbq('track', 'PageLeave', {
      page_visit_duration: Math.round(visitDuration),
      session_duration: Math.round(sessionDuration),
      url: window.location.href,
      title: document.title
    });
  }
  
  // Clear any pending timeouts
  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
  }
}

/**
 * Handle visibility change (tab switching)
 */
function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    // User switched away from the page
    trackPageLeave();
  } else if (document.visibilityState === 'visible') {
    // User switched back to the page
    pageVisitStartTime = Date.now();
    trackPageView();
  }
}

/**
 * Set up periodic heartbeat tracking
 */
function setupHeartbeatTracking() {
  // Send a heartbeat every 30 seconds to track continued engagement
  setInterval(() => {
    const visitDuration = (Date.now() - pageVisitStartTime) / 1000; // in seconds
    const sessionDuration = (Date.now() - sessionStartTime) / 1000; // in seconds
    
    // Track engagement if user has been on page for at least 10 seconds
    if (visitDuration >= 10 && window.fbq) {
      window.fbq('track', 'Engagement', {
        engagement_time: Math.round(visitDuration),
        session_duration: Math.round(sessionDuration),
        url: window.location.href,
        title: document.title
      });
    }
  }, 30000); // 30 seconds
}

/**
 * Track PageView event
 * Call this when user navigates to a new page
 */
export const trackPageView = () => {
  // Update page visit start time for the new page
  pageVisitStartTime = Date.now();
  
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
};

/**
 * Track ViewContent event
 * Call this when user views a product page
 * @param {Object} product - Product information
 */
export const trackViewContent = (product) => {
  if (!window.fbq) return;

  const eventData = {
    content_name: product.title || product.name,
    content_ids: [product._id || product.id],
    content_type: 'product',
    value: product.discountedPrice || product.price || 0,
    currency: 'INR',
  };

  if (product.category) {
    eventData.content_category = product.category;
  }

  window.fbq('track', 'ViewContent', eventData);
  // console.log('Meta Pixel: ViewContent tracked', eventData);
};

/**
 * Track AddToCart event
 * Call this when user adds product to cart
 * @param {Object} product - Product information
 * @param {number} quantity - Quantity added
 */
export const trackAddToCart = (product, quantity = 1) => {
  if (!window.fbq) return;

  const price = product.discountedPrice || product.price || 0;
  const eventData = {
    content_name: product.title || product.name,
    content_ids: [product._id || product.id],
    content_type: 'product',
    value: price * quantity,
    currency: 'INR',
    quantity: quantity,
  };

  if (product.category) {
    eventData.content_category = product.category;
  }

  window.fbq('track', 'AddToCart', eventData);
  // console.log('Meta Pixel: AddToCart tracked', eventData);
};

/**
 * Track InitiateCheckout event
 * Call this when user starts the checkout process
 * @param {Array} cartItems - Array of cart items
 * @param {number} totalValue - Total cart value
 */
export const trackInitiateCheckout = (cartItems, totalValue) => {
  if (!window.fbq) return;

  const contentIds = cartItems.map(item => {
    // Handle both authenticated and guest cart structures
    if (item.productId?._id) return item.productId._id;
    if (item.productId) return item.productId;
    return item._id || item.id;
  });

  const eventData = {
    content_ids: contentIds,
    content_type: 'product',
    value: totalValue || 0,
    currency: 'INR',
    num_items: cartItems.length,
  };

  window.fbq('track', 'InitiateCheckout', eventData);
  // console.log('Meta Pixel: InitiateCheckout tracked', eventData);
};

/**
 * Track AddPaymentInfo event
 * Call this when user adds payment information
 * @param {number} totalValue - Total order value
 */
export const trackAddPaymentInfo = (totalValue) => {
  if (!window.fbq) return;

  const eventData = {
    value: totalValue || 0,
    currency: 'INR',
  };

  window.fbq('track', 'AddPaymentInfo', eventData);
  // console.log('Meta Pixel: AddPaymentInfo tracked', eventData);
};

/**
 * Track Purchase event
 * Call this when an order is successfully completed
 * @param {Object} orderData - Order information
 */
export const trackPurchase = (orderData) => {
  if (!window.fbq) return;

  const contentIds = orderData.orderItems?.map(item => {
    return item.product || item.productId || item._id;
  }) || [];

  const eventData = {
    content_ids: contentIds,
    content_type: 'product',
    value: orderData.totalPrice || orderData.totalPriceAfterDiscount || 0,
    currency: 'INR',
    num_items: orderData.orderItems?.length || 0,
  };

  // Add order ID if available
  if (orderData._id || orderData.orderId) {
    eventData.order_id = orderData._id || orderData.orderId;
  }

  window.fbq('track', 'Purchase', eventData);
  // console.log('Meta Pixel: Purchase tracked', eventData);
};

/**
 * Track AddToWishlist event
 * Call this when user adds product to wishlist
 * @param {Object} product - Product information
 */
export const trackAddToWishlist = (product) => {
  if (!window.fbq) return;

  const eventData = {
    content_name: product.title || product.name,
    content_ids: [product._id || product.id],
    content_type: 'product',
    value: product.discountedPrice || product.price || 0,
    currency: 'INR',
  };

  if (product.category) {
    eventData.content_category = product.category;
  }

  window.fbq('track', 'AddToWishlist', eventData);
  // console.log('Meta Pixel: AddToWishlist tracked', eventData);
};

/**
 * Track Search event
 * Call this when user performs a search
 * @param {string} searchQuery - Search query string
 */
export const trackSearch = (searchQuery) => {
  if (!window.fbq) return;

  const eventData = {
    search_string: searchQuery,
    content_type: 'product',
  };

  window.fbq('track', 'Search', eventData);
  // console.log('Meta Pixel: Search tracked', eventData);
};

/**
 * Track Lead event
 * Call this when user submits a contact form or signs up
 * @param {Object} data - Lead information
 */
export const trackLead = (data = {}) => {
  if (!window.fbq) return;

  const eventData = {
    content_name: data.formName || 'Contact Form',
  };

  window.fbq('track', 'Lead', eventData);
  // console.log('Meta Pixel: Lead tracked', eventData);
};

/**
 * Track CompleteRegistration event
 * Call this when user completes signup/registration
 * @param {string} method - Registration method (e.g., 'email', 'google')
 */
export const trackCompleteRegistration = (method = 'email') => {
  if (!window.fbq) return;

  const eventData = {
    content_name: 'User Registration',
    status: 'completed',
    registration_method: method,
  };

  window.fbq('track', 'CompleteRegistration', eventData);
  // console.log('Meta Pixel: CompleteRegistration tracked', eventData);
};

/**
 * Track custom event
 * Use this for tracking custom events specific to your business
 * @param {string} eventName - Custom event name
 * @param {Object} eventData - Event data
 */
export const trackCustomEvent = (eventName, eventData = {}) => {
  if (!window.fbq) return;

  window.fbq('trackCustom', eventName, eventData);
  // console.log(`Meta Pixel: Custom event "${eventName}" tracked`, eventData);
};

/**
 * Check if Meta Pixel is loaded and ready
 * @returns {boolean} - True if pixel is ready
 */
export const isPixelReady = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

/**
 * Track product listing view
 * Call this when user views a category or product listing page
 * @param {Array} products - Array of products displayed
 * @param {string} category - Category name (optional)
 */
export const trackViewCategory = (products = [], category = '') => {
  if (!window.fbq || !products.length) return;

  const contentIds = products.map(p => p._id || p.id).filter(Boolean);
  
  const eventData = {
    content_ids: contentIds,
    content_type: 'product',
  };

  if (category) {
    eventData.content_category = category;
  }

  // Use ViewContent for category views
  window.fbq('track', 'ViewContent', eventData);
  // console.log('Meta Pixel: ViewCategory tracked', eventData);
};

// Export all tracking functions
export default {
  initMetaPixel,
  trackPageView,
  trackViewContent,
  trackAddToCart,
  trackInitiateCheckout,
  trackAddPaymentInfo,
  trackPurchase,
  trackAddToWishlist,
  trackSearch,
  trackLead,
  trackCompleteRegistration,
  trackCustomEvent,
  isPixelReady,
  trackViewCategory,
};
